<template>
	<div
		class="job-hierarchy-box tw-flex tw-flex-col tw-items-center tw-justify-center"
		:class="{
		'has-error': assignationHasError,
		'selected': selected,
		'clickable': clickable,
		'no-father': hasNoFather,
	}"
		@click="onSelectedClick"
	>
		<template v-if="showCheckbox">
			<check-icon-toggle
				class="check-assignation"
				@click="onAssignClicked"
				:checked="isAssignedToSelected"
			/>
		</template>
		<v-icon
			v-else-if="showMirrorAction"
			class="mirror-assignation"
			:class="{ 'mirror-active': isMirroredToSelected }"
			size="20"
			:title="isMirroredToSelected ? 'Quitar de esta área' : 'Mostrar también en esta área'"
			@click.native.stop="onMirrorClicked"
		>
			device_hub
		</v-icon>
		<v-icon
			v-if="selected"
			class="close-assignation"
			size="16"
			title="Cerrar"
			@click.native.stop="$emit('close')"
		>
			close
		</v-icon>
		<p class="tw-m-0 tw-text-center">{{ jobPosition.name }}</p>
		<p class="tw-m-0 tw-text-center job-father" v-if="jobPosition.fatherName">({{ jobPosition.fatherName }})</p>
		<p class="tw-m-0 tw-text-center area-line" v-if="showAreaLabel">
			<v-icon size="14" :class="isFromOtherArea ? 'area-icon-foreign' : 'area-icon'">business</v-icon>
			<span :class="isFromOtherArea ? 'area-label-foreign' : 'area-label'">{{ areaName }}</span>
		</p>
		<div v-if="assignationHasError" class="tw-mt-2">
			<edit-icon color="error" @click="onEditClick" class="edit-button" />
		</div>
		<job-selector-edition-dialog v-if="assignationHasError" ref="jobSelectorEditionDialog" />
	</div>
</template>

<script>
import EditIcon from '@/modules/shared/components/icons/edit-icon';
import JobSelectorEditionDialog
	from '@/modules/job-evaluation/components/supplies/guideline-model/hierarchy/hierarchy-assignation/job-selector-edition-dialog';
import CheckIcon from '@/modules/shared/components/icons/check-icon';

import { assoc, pipe, pathOr } from 'ramda';
import { mapState } from 'vuex';
import CheckIconToggle from '@/modules/shared/components/icons/check-icon-toggle';

export default {
	name: 'job-hierarchy-box',
	components: {
		CheckIconToggle,
		CheckIcon,
		JobSelectorEditionDialog,
		EditIcon,
	},
	props: [
		'assignationHasError',
		'jobPosition',
		'selectedJobPosition',
		'selected',
		'assignationId',
		'selectable',
		'clickable',
		'filteredAreaId',
		'level',
	],
	computed: {
		...mapState('jobEvaluation', {
			hierarchyAssignation: pathOr([], ['supplies', 'guidelineModel', 'hierarchyAssignation']),
			groups: pathOr([], ['supplies', 'guidelineModel', 'groups']),
		}),
		isFromOtherArea() {
			return this.filteredAreaId && this.jobPosition.areaId !== this.filteredAreaId;
		},
		areaName() {
			const group = (this.groups || []).find(g => g.id === this.jobPosition.areaId);
			return (group && group.name) || 'Sin área';
		},
		showAreaLabel() {
			return !this.filteredAreaId || this.isFromOtherArea;
		},
		hasNoFather() {
			return this.level >= 1
				&& this.jobPosition.typeVerb
				&& !this.jobPosition.fatherId
				&& !this.jobPosition.isHighestAuthority;
		},
		isAssignedToSelected() {
			return this.jobPosition.fatherId && this.jobPosition.fatherId === this.selectedJobPosition.id;
		},
		hasOtherFather() {
			return this.jobPosition.fatherId &&
				this.jobPosition.fatherId !== this.selectedJobPosition.id;
		},
		mirrorFatherIds() {
			return this.jobPosition.mirrorFatherIds || [];
		},
		isMirroredToSelected() {
			return this.mirrorFatherIds.indexOf(this.selectedJobPosition.id) !== -1;
		},
		showCheckbox() {
			if (!this.selectedJobPosition.id || !this.selectable) return false;
			// Cannot be its own father
			if (this.jobPosition.id === this.selectedJobPosition.id) return false;
			// The highest authority can never be a child of anyone
			if (this.jobPosition.isHighestAuthority) return false;
			// Single-father discipline: a job that already reports to a DIFFERENT boss
			// is not assignable as a primary child (a mirror action is offered instead).
			if (this.hasOtherFather) return false;
			// Assignable/unassignable regardless of area (single-father lock applied above).
			// Cross-area subordinates may have a parent in any area.
			return true;
		},
		// A job that already reports to a DIFFERENT boss can still be "mirrored" under the
		// selected boss: it is drawn under both in the org chart without changing its real
		// reporting line. Offered only when the job already has another primary father.
		showMirrorAction() {
			if (!this.selectedJobPosition.id || !this.selectable) return false;
			if (this.jobPosition.id === this.selectedJobPosition.id) return false;
			if (this.jobPosition.isHighestAuthority) return false;
			return this.hasOtherFather;
		},
	},
	methods: {
		onEditClick() {
			this.$refs.jobSelectorEditionDialog.show(this.jobPosition);
		},
		onAssignClicked() {
			// Toggle: already reports to the selected boss -> unassign; otherwise assign/move
			if (this.isAssignedToSelected) {
				this.dispatchAssignation(null, null);
				return;
			}
			if (this.wouldCreateCycle()) {
				this.showNotification(
					'No se puede asignar: crearía un ciclo en la jerarquía.',
					'warning',
				);
				return;
			}
			this.dispatchAssignation(this.selectedJobPosition.id, this.selectedJobPosition.name);
		},
		dispatchAssignation(fatherId, fatherName) {
			this.$store.dispatch('jobEvaluation/updateAssignation', {
				assignationId: this.assignationId,
				jobPosition: pipe(
					assoc('fatherId', fatherId),
					assoc('fatherName', fatherName),
				)(this.jobPosition),
			});
		},
		// Mirror toggle: make this job ALSO appear under the selected boss in the org chart
		// without removing it from its real father. Click again to remove the mirror.
		onMirrorClicked() {
			if (this.isMirroredToSelected) {
				this.dispatchMirror(
					this.mirrorFatherIds.filter(id => id !== this.selectedJobPosition.id),
				);
				return;
			}
			if (this.wouldCreateCycle()) {
				this.showNotification(
					'No se puede espejar: crearía un ciclo en la jerarquía.',
					'warning',
				);
				return;
			}
			this.dispatchMirror(this.mirrorFatherIds.concat(this.selectedJobPosition.id));
		},
		dispatchMirror(mirrorFatherIds) {
			this.$store.dispatch('jobEvaluation/updateAssignation', {
				assignationId: this.assignationId,
				jobPosition: assoc('mirrorFatherIds', mirrorFatherIds, this.jobPosition),
			});
		},
		// Assigning this job to the selected boss would create a cycle if THIS job is
		// already an ancestor of the selected boss. Walk the father chain upward.
		wouldCreateCycle() {
			const jobById = {};
			this.hierarchyAssignation.forEach((level) => {
				const jobs = level.jobPositions || [];
				for (let i = 0; i < jobs.length; i += 1) {
					jobById[jobs[i].id] = jobs[i];
				}
			});
			let current = this.selectedJobPosition.id;
			const visited = {};
			while (current) {
				if (current === this.jobPosition.id) return true;
				if (visited[current]) break;
				visited[current] = true;
				const node = jobById[current];
				current = node ? node.fatherId : null;
			}
			return false;
		},
		onSelectedClick() {
			if (this.clickable && this.selectable) {
				this.$emit('click');
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.job-hierarchy-box {
	padding: 20px;
	color: var(--app-color-primary);
	font-weight: bold;
	border: 1px solid var(--app-color-primary);
	border-radius: 10px;
	width: 200px;
	min-height: 80px;
	user-select: none;
	position: relative;
	.edit-button {
		cursor: pointer;
	}
	.job-father {
			color: var(--app-color-info);
			font-size: 12px;
			font-weight: normal;
		}
	.area-line {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 4px;
	}
	.area-icon {
		color: var(--app-color-font-gray) !important;
	}
	.area-icon-foreign {
		color: var(--app-color-secondary) !important;
	}
	.area-label {
		color: var(--app-color-font-gray);
		font-size: 11px;
		font-weight: 600;
	}
	.area-label-foreign {
		color: var(--app-color-secondary);
		font-size: 11px;
		font-style: italic;
	}
	.check-assignation {
		position: absolute;
		top: 4px;
		left: 4px;
	}
	.close-assignation {
		position: absolute;
		top: 4px;
		right: 4px;
		cursor: pointer;
		color: white !important;
		opacity: 0.7;
		&:hover {
			opacity: 1;
		}
	}
	.mirror-assignation {
		position: absolute;
		top: 4px;
		left: 4px;
		cursor: pointer;
		color: var(--app-color-secondary);
	}
	.mirror-assignation.mirror-active {
		color: #f09c2b !important;
	}
	.assign {
		cursor: pointer;
	}
	&.clickable {
		cursor: pointer;
	}
	&.has-error {
		border: 1px solid var(--app-color-error);
		color: var(--app-color-error);
	}
	&.no-father {
		box-shadow: inset 0 0 0 2px var(--app-color-warning);
	}
	&.selected {
		background-color: var(--app-color-primary);
		color: white;
		.job-father,
		.area-label,
		.area-label-foreign {
			color: white;
		}
		.area-icon,
		.area-icon-foreign {
			color: white !important;
		}
	}
}
</style>



// WEBPACK FOOTER //
// src/modules/job-evaluation/components/supplies/guideline-model/hierarchy/hierarchy-assignation/job-hierarchy-box.vue